import { TypedDocumentNode, gql } from '@apollo/client';
import { lazy } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { StuffInRewardBoxType } from '@sorare/core/src/components/rewards/RewardBoxDialog/types';
import { RewardCount } from '@sorare/core/src/components/rewards/RewardCount';
import { isType } from '@sorare/core/src/gql';
import { rivals } from '@sorare/core/src/lib/glossary';

import {
  ArenaTicketInRewardBox_ProbabilisticBundleSlotItemInterface,
  ArenaTicketInRewardBox_ProbabilisticBundleSlotItemInterface_ProbabilisticBundleSlotArenaTicketItem_,
} from './__generated__/index.graphql';

const ArenaTicketInRewardBoxTrois = lazy(
  async () =>
    import('@sorare/trois/src/components/RewardBox/ArenaTicketInRewardBox')
);

const NoWrap = styled.div`
  white-space: nowrap;
`;

type Props = {
  item: ArenaTicketInRewardBox_ProbabilisticBundleSlotItemInterface;
  visible: boolean;
};

const ArenaTicketInRewardBoxCanHandle = (
  item: Props['item']
): item is ArenaTicketInRewardBox_ProbabilisticBundleSlotItemInterface_ProbabilisticBundleSlotArenaTicketItem_ => {
  return isType(item, 'ProbabilisticBundleSlotArenaTicketItem');
};

export const ArenaTicketInRewardBox: StuffInRewardBoxType<
  ArenaTicketInRewardBox_ProbabilisticBundleSlotItemInterface
> = ({ item, visible }: Props) => {
  const { formatMessage } = useIntl();

  if (!ArenaTicketInRewardBoxCanHandle(item)) {
    return null;
  }

  return (
    <ArenaTicketInRewardBoxTrois
      visible={visible}
      quantity={item.quantity}
      description={
        <NoWrap>
          <RewardCount
            quantity={item.quantity}
            rewardName={formatMessage(rivals.arenaTicket)}
          />
        </NoWrap>
      }
    />
  );
};

ArenaTicketInRewardBox.fragments = {
  ProbabilisticBundleSlotItemInterface: gql`
    fragment ArenaTicketInRewardBox_ProbabilisticBundleSlotItemInterface on ProbabilisticBundleSlotItemInterface {
      id
      ... on ProbabilisticBundleSlotArenaTicketItem {
        id
        quantity
      }
    }
  ` as TypedDocumentNode<ArenaTicketInRewardBox_ProbabilisticBundleSlotItemInterface>,
};

const ArenaTicketInRewardBoxPreloader = () => {
  return null;
};

ArenaTicketInRewardBox.preloader = ArenaTicketInRewardBoxPreloader;

ArenaTicketInRewardBox.canHandle = ArenaTicketInRewardBoxCanHandle;

ArenaTicketInRewardBox.description = (item: Props['item']) => {
  if (!ArenaTicketInRewardBoxCanHandle(item)) {
    return null;
  }
  return `${item.quantity}x Arena tickets`;
};

ArenaTicketInRewardBox.fallback = (item: Props['item']) => {
  if (!ArenaTicketInRewardBoxCanHandle(item)) {
    return null;
  }
  return <div>{ArenaTicketInRewardBox.description(item)}</div>;
};
